import Ionicons from 'react-native-vector-icons/Ionicons';
import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Linking,
    ScrollView,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../api/client';
import { getThemeColors } from '../utils/themeStyles';
import ErrorRetry from '../components/ErrorRetry';
import ActionLoader from '../components/ActionLoader';
import { useT } from '../i18n/useT';

interface Invoice {
  id: string;
  date: string;
  description: string;
  amount: number;
  status: 'paid' | 'pending' | 'failed';
  paymentMethod: string;
  pdfUrl?: string | null;
  items: Array<{ name: string; quantity: number; price: number }>;
}

export default function InvoicesScreen() {
  const t = useT();
  const { isDarkTheme } = useTheme();
  const { user, getAccessToken } = useAuth();
  const colors = getThemeColors(isDarkTheme);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Chargement DB : GET /me/invoices (utilisateur connecté).
  const loadInvoices = useCallback(async () => {
    const token = getAccessToken();
    if (!user || !token) { setInvoices([]); setError(false); setLoading(false); return; }
    setLoading(true);
    try {
      const data = await apiFetch<{ invoices: Invoice[] }>('/me/invoices', { token, auth: true });
      setInvoices(data?.invoices ?? []);
      setError(false);
    } catch (_) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user, getAccessToken]);

  useEffect(() => { void loadInvoices(); }, [loadInvoices]);

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = inv.description.toLowerCase().includes(searchTerm.toLowerCase()) || inv.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) { case 'paid': return '#10B981'; case 'pending': return '#F59E0B'; case 'failed': return '#EF4444'; default: return '#6B7280'; }
  };
  const getStatusLabel = (status: string) => {
    switch (status) { case 'paid': return 'Paid'; case 'pending': return 'Pending'; case 'failed': return 'Failed'; default: return status; }
  };

  const handleDownload = (invoice: Invoice) => {
    if (invoice.pdfUrl) { void Linking.openURL(invoice.pdfUrl); return; }
    Alert.alert('Facture', `Aucun PDF disponible pour ${invoice.id}.`);
  };

  const renderInvoiceDetail = () => {
    if (!selectedInvoice) return null;
    const inv = selectedInvoice;
    const subtotal = inv.items.reduce((s, i) => s + i.price * i.quantity, 0);
    const total = inv.amount;
    const tax = Math.max(0, total - subtotal);

    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
          <TouchableOpacity onPress={() => setSelectedInvoice(null)}>
            <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>Invoice {inv.id}</Text>
          <TouchableOpacity onPress={() => handleDownload(inv)}>
            <Ionicons name="download" size={24} color={colors.iconPrimary} />
          </TouchableOpacity>
        </View>
        <ScrollView style={[styles.content, { backgroundColor: colors.background }]}>
          <View style={[styles.invoiceDetailHeader, { borderBottomColor: colors.border }]}>
            <View style={styles.invoiceInfo}>
              <Text style={[styles.invoiceTitle, { color: colors.textPrimary }]}>{t('invoices.issuer')}</Text>
              <Text style={[styles.invoiceAddress, { color: colors.textSecondary }]}>123 Tech Street{'\n'}San Francisco, CA 94105{'\n'}United States</Text>
            </View>
            <View style={[styles.statusBadge, { backgroundColor: getStatusColor(inv.status) }]}>
              <Text style={styles.statusBadgeText}>{getStatusLabel(inv.status)}</Text>
            </View>
          </View>
          <View style={[styles.invoiceDetails, { borderBottomColor: colors.border }]}>
            <View style={styles.detailRow}>
              <Text style={[styles.detailLabel, { color: colors.textPrimary }]}>{t('invoices.billTo')}</Text>
              <Text style={[styles.detailValue, { color: colors.textSecondary }]}>{[user?.prenom, user?.nom].filter(Boolean).join(' ') || 'Client'}{'\n'}{user?.email || ''}</Text>
            </View>
            <View style={styles.detailRow}>
              <Text style={[styles.detailLabel, { color: colors.textPrimary }]}>{t('invoices.details')}</Text>
              <Text style={[styles.detailValue, { color: colors.textSecondary }]}>Invoice #: {inv.id}{'\n'}Date: {new Date(inv.date).toLocaleDateString()}{'\n'}Payment: {inv.paymentMethod}</Text>
            </View>
          </View>
          <View style={[styles.itemsSection, { borderTopColor: colors.border }]}>
            <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>{t('invoices.items')}</Text>
            {inv.items.map((item, index) => (
              <View key={index} style={[styles.itemRow, { borderBottomColor: colors.border }]}>
                <View style={styles.itemInfo}>
                  <Text style={[styles.itemName, { color: colors.textPrimary }]}>{item.name}</Text>
                  <Text style={[styles.itemQuantity, { color: colors.textSecondary }]}>Qty: {item.quantity}</Text>
                </View>
                <Text style={[styles.itemPrice, { color: colors.textPrimary }]}>${item.price.toFixed(2)}</Text>
              </View>
            ))}
            <View style={[styles.totalSection, { borderTopColor: colors.border }]}>
              <View style={styles.totalRow}>
                <Text style={[styles.totalLabel, { color: colors.textSecondary }]}>{t('invoices.subtotal')}</Text>
                <Text style={[styles.totalValue, { color: colors.textPrimary }]}>${subtotal.toFixed(2)}</Text>
              </View>
              <View style={styles.totalRow}>
                <Text style={[styles.totalLabel, { color: colors.textSecondary }]}>{t('invoices.tax')}</Text>
                <Text style={[styles.totalValue, { color: colors.textPrimary }]}>${tax.toFixed(2)}</Text>
              </View>
              <View style={[styles.totalRow, styles.finalTotal, { borderTopColor: colors.border }]}>
                <Text style={[styles.finalTotalLabel, { color: colors.textPrimary }]}>{t('invoices.total')}</Text>
                <Text style={[styles.finalTotalValue, { color: colors.textPrimary }]}>${total.toFixed(2)}</Text>
              </View>
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    );
  };

  if (selectedInvoice) return renderInvoiceDetail();

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>{t('invoices.title')}</Text>
        <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>{t('invoices.subtitle')}</Text>
      </View>

      <View style={[styles.searchSection, { backgroundColor: colors.background }]}>
        <View style={[styles.searchContainer, { backgroundColor: colors.inputBackground, borderColor: colors.inputBorder }]}>
          <Ionicons name="search" size={16} color={colors.iconSecondary} />
          <TextInput
            style={[styles.searchInput, { color: colors.inputText }]}
            placeholder={t('invoices.search')}
            placeholderTextColor={colors.inputPlaceholder}
            value={searchTerm}
            onChangeText={setSearchTerm}
          />
        </View>
        <View style={styles.filterContainer}>
          <Ionicons name="filter" size={16} color={colors.iconSecondary} />
          <TouchableOpacity
            style={[styles.filterButton, { backgroundColor: colors.inputBackground, borderColor: colors.inputBorder }]}
            onPress={() => {
              const options = ['All Status', 'Paid', 'Pending', 'Failed'];
              Alert.alert(t('invoices.filterByStatus'), t('invoices.selectStatus'), options.map(opt => ({ text: opt, onPress: () => setStatusFilter(opt === 'All Status' ? 'all' : opt.toLowerCase()) })));
            }}
          >
            <Text style={[styles.filterButtonText, { color: colors.textPrimary }]}>
              {statusFilter === 'all' ? 'All Status' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView style={[styles.content, { backgroundColor: colors.background }]}>
        {error && invoices.length === 0 ? (
          <ErrorRetry onRetry={() => void loadInvoices()} retrying={loading} message={t('invoices.loadFailed')} />
        ) : loading && invoices.length === 0 ? (
          <View style={styles.emptyState}>
            <ActionLoader label={t('invoices.loading')} />
          </View>
        ) : filteredInvoices.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="document-text" size={64} color={colors.textTertiary} />
            <Text style={[styles.emptyTitle, { color: colors.textPrimary }]}>{t('invoices.empty')}</Text>
            <Text style={[styles.emptySubtitle, { color: colors.textSecondary }]}>
              {t(searchTerm || statusFilter !== 'all' ? 'invoices.noMatch' : 'invoices.noPurchase')}
            </Text>
          </View>
        ) : (
          <View style={styles.invoiceList}>
            {filteredInvoices.map((invoice) => (
              <View key={invoice.id} style={[styles.invoiceCard, { backgroundColor: colors.cardBackground }]}>
                <View style={styles.invoiceHeader}>
                  <View style={styles.invoiceInfo}>
                    <Text style={[styles.invoiceId, { color: colors.textPrimary }]}>{invoice.id}</Text>
                    <Text style={[styles.invoiceDate, { color: colors.textSecondary }]}>{new Date(invoice.date).toLocaleDateString()}</Text>
                  </View>
                  <View style={styles.invoiceAmount}>
                    <Text style={[styles.amountText, { color: colors.textPrimary }]}>${invoice.amount.toFixed(2)}</Text>
                    <View style={[styles.statusBadge, { backgroundColor: getStatusColor(invoice.status) }]}>
                      <Text style={styles.statusBadgeText}>{getStatusLabel(invoice.status)}</Text>
                    </View>
                  </View>
                </View>
                <Text style={[styles.invoiceDescription, { color: colors.textSecondary }]}>{invoice.description}</Text>
                <Text style={[styles.paymentMethod, { color: colors.textTertiary }]}>Paid with {invoice.paymentMethod}</Text>
                <View style={styles.invoiceActions}>
                  <TouchableOpacity style={[styles.actionButton, { borderColor: colors.border }]} onPress={() => setSelectedInvoice(invoice)}>
                    <Ionicons name="eye" size={16} color={colors.iconPrimary} />
                    <Text style={[styles.actionButtonText, { color: colors.textPrimary }]}>{t('invoices.view')}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[styles.actionButton, { borderColor: colors.border }]} onPress={() => handleDownload(invoice)}>
                    <Ionicons name="download" size={16} color={colors.iconPrimary} />
                    <Text style={[styles.actionButtonText, { color: colors.textPrimary }]}>{t('invoices.download')}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: { padding: 16, borderBottomWidth: 1 },
  headerTitle: { fontSize: 24, fontWeight: 'bold', marginBottom: 4 },
  headerSubtitle: { fontSize: 14 },
  searchSection: { padding: 16, gap: 12 },
  searchContainer: { flexDirection: 'row', alignItems: 'center', borderRadius: 8, paddingHorizontal: 12, borderWidth: 1 },
  searchInput: { flex: 1, paddingVertical: 12, paddingHorizontal: 8, fontSize: 16 },
  filterContainer: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  filterButton: { flex: 1, borderRadius: 8, paddingVertical: 12, paddingHorizontal: 16, borderWidth: 1 },
  filterButtonText: { fontSize: 14 },
  content: { flex: 1, padding: 16 },
  emptyState: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 64 },
  emptyTitle: { fontSize: 18, fontWeight: 'bold', marginTop: 16, marginBottom: 8 },
  emptySubtitle: { fontSize: 14, textAlign: 'center' },
  invoiceList: { gap: 12 },
  invoiceCard: { borderRadius: 12, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.1, shadowRadius: 3, elevation: 2 },
  invoiceHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 },
  invoiceInfo: { flex: 1 },
  invoiceId: { fontSize: 16, fontWeight: '600', marginBottom: 4 },
  invoiceDate: { fontSize: 14 },
  invoiceAmount: { alignItems: 'flex-end' },
  amountText: { fontSize: 16, fontWeight: 'bold', marginBottom: 4 },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4 },
  statusBadgeText: { fontSize: 12, fontWeight: '600', color: '#FFFFFF' },
  invoiceDescription: { fontSize: 14, marginBottom: 8 },
  paymentMethod: { fontSize: 12, marginBottom: 12 },
  invoiceActions: { flexDirection: 'row', gap: 8 },
  actionButton: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 8, borderWidth: 1, borderRadius: 6, gap: 4 },
  actionButtonText: { fontSize: 14 },
  invoiceDetailHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottomWidth: 1 },
  invoiceTitle: { fontSize: 18, fontWeight: 'bold', marginBottom: 8 },
  invoiceAddress: { fontSize: 14, lineHeight: 20 },
  invoiceDetails: { padding: 16, gap: 16, borderBottomWidth: 1 },
  detailRow: { gap: 8 },
  detailLabel: { fontSize: 14, fontWeight: '600' },
  detailValue: { fontSize: 14, lineHeight: 20 },
  itemsSection: { padding: 16, borderTopWidth: 1 },
  sectionTitle: { fontSize: 16, fontWeight: 'bold', marginBottom: 12 },
  itemRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1 },
  itemInfo: { flex: 1 },
  itemName: { fontSize: 14, fontWeight: '500', marginBottom: 2 },
  itemQuantity: { fontSize: 12 },
  itemPrice: { fontSize: 14, fontWeight: '600' },
  totalSection: { marginTop: 16, paddingTop: 16, borderTopWidth: 1, gap: 8 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  totalLabel: { fontSize: 14 },
  totalValue: { fontSize: 14 },
  finalTotal: { borderTopWidth: 1, paddingTop: 8, marginTop: 8 },
  finalTotalLabel: { fontSize: 16, fontWeight: 'bold' },
  finalTotalValue: { fontSize: 16, fontWeight: 'bold' },
});
